/**
 * How a page's move this period reads, wherever it is shown: `/review`'s page header, the
 * Dashboard's page row and CHANGE column, the Issues card's evidence line and the export. One rule,
 * so the Dashboard says what `/review` says (TECHNICAL_DEBT §0ck).
 *
 * The diff layer renders every number the same way, `current (signed change)`. For traffic that
 * reads right: "120 (-30)" is a loss. For a Google position it reads backwards: a page that fell
 * from #6 to #7 came out as "7 (+1)", under "lost", which reads like a gain (owner, 2026-11-03). A
 * position is said in words, with both places, so the direction does not depend on knowing that a
 * lower number is better. The shape is also unlike the Search Console click lines ("25 (-5)"), which
 * sit beside it on the same header and must not read as a second click figure.
 */

/** The movers' rank metric for a Google position (`crawl-run-movers.ts`). */
export const SERP_POSITION_METRIC_KEY = 'serpPosition';

/** The slice of a period change this rule reads. */
export interface PeriodChangeLineInput {
  /** Absent on a brief composed before the field was sent; the line then falls back to `display`. */
  metricKey?: string | null;
  previous?: number | null;
  current?: number | null;
  /** The diff layer's own rendering, used as it is for every other metric. */
  display: string;
}

function place(n: number): string {
  return `#${Number.isInteger(n) ? n : n.toFixed(1)}`;
}

/** Whether this change is a Google position, which reads in words rather than as a signed number. */
export function periodChangeIsPosition(change: Pick<PeriodChangeLineInput, 'metricKey'>): boolean {
  return change.metricKey === SERP_POSITION_METRIC_KEY;
}

/**
 * The change as a reader sees it. A Google position: "fell from #6 to #7", "rose from #7 to #6".
 * Anything else, or a position missing either side: the diff layer's `display`, unchanged.
 */
export function periodChangeValue(change: PeriodChangeLineInput): string {
  const { previous, current } = change;
  if (
    !periodChangeIsPosition(change) ||
    typeof previous !== 'number' ||
    typeof current !== 'number' ||
    !Number.isFinite(previous) ||
    !Number.isFinite(current)
  ) {
    return change.display;
  }
  if (current === previous) return `unchanged at ${place(current)}`;
  return `${current > previous ? 'fell' : 'rose'} from ${place(previous)} to ${place(current)}`;
}
